import {
  CustomAttribute,
  CustomAttributesFormData,
  CustomAttributeType,
  SelectOption,
} from '../types/custom-attribute';
import { CreateAttributeValueRequest } from '../types/dto/request/custom-attribute-request';
import {
  AttributeValueResponse,
  CustomAttributeResponse,
  CustomAttributeTypeDTO,
  SelectOptionResponse,
} from '../types/dto/response/custom-attribute-response';

export const normalizeAttributeKey = (name: string): string =>
  name.toLowerCase().replace(/\s+/g, '');

const isSelectType = (type: CustomAttributeType): boolean =>
  type === CustomAttributeType.Select || type === CustomAttributeType.MultiSelect;

const mapSelectOption = (opt: SelectOptionResponse): SelectOption => ({
  id: opt.id,
  value: opt.value,
});

export const mapCustomAttributeTypeFromDTO = (dto: CustomAttributeTypeDTO): CustomAttributeType => {
  switch (dto) {
    case CustomAttributeTypeDTO.STRING:
      return CustomAttributeType.Text;
    case CustomAttributeTypeDTO.NUMBER:
      return CustomAttributeType.Number;
    case CustomAttributeTypeDTO.BOOLEAN:
      return CustomAttributeType.Boolean;
    case CustomAttributeTypeDTO.DATE:
      return CustomAttributeType.Date;
    case CustomAttributeTypeDTO.SINGLESELECT:
      return CustomAttributeType.Select;
    case CustomAttributeTypeDTO.MULTISELECT:
      return CustomAttributeType.MultiSelect;
    default:
      return CustomAttributeType.Text;
  }
};

export const mapCustomAttributeTypeToDTO = (type: CustomAttributeType): CustomAttributeTypeDTO => {
  switch (type) {
    case CustomAttributeType.Text:
      return CustomAttributeTypeDTO.STRING;
    case CustomAttributeType.Number:
      return CustomAttributeTypeDTO.NUMBER;
    case CustomAttributeType.Boolean:
      return CustomAttributeTypeDTO.BOOLEAN;
    case CustomAttributeType.Date:
      return CustomAttributeTypeDTO.DATE;
    case CustomAttributeType.Select:
      return CustomAttributeTypeDTO.SINGLESELECT;
    case CustomAttributeType.MultiSelect:
      return CustomAttributeTypeDTO.MULTISELECT;
    default:
      return CustomAttributeTypeDTO.STRING;
  }
};

/**
 * Form data → API request. For select types, converts form option IDs to `optionIds`.
 */
export const mapCustomAttributesToDTO = (
  formData: CustomAttributesFormData,
  availableAttributes: CustomAttribute[]
): CreateAttributeValueRequest[] => {
  const result: CreateAttributeValueRequest[] = [];

  const attributeMap = new Map<string, CustomAttribute>();
  availableAttributes.forEach(attr => {
    attributeMap.set(normalizeAttributeKey(attr.name), attr);
  });

  Object.entries(formData).forEach(([key, value]) => {
    const attribute = attributeMap.get(key);
    if (!attribute) return;

    if (isSelectType(attribute.type)) {
      // Normalize to an array of raw values, then drop empties (a cleared
      // SingleSelect leaves '' in form state — Number('') is 0, which the backend
      // rejects). Only keep finite numeric option IDs.
      const rawValues = Array.isArray(value) ? value : value == null ? [] : [value];
      const ids = rawValues
        .filter(v => v !== '' && v != null)
        .map(Number)
        .filter(id => Number.isFinite(id));
      result.push({ attributeId: attribute.id, optionIds: ids });
    } else {
      const scalar = Array.isArray(value) ? null : (value ?? null);
      result.push({ attributeId: attribute.id, value: scalar });
    }
  });

  return result;
};

export const mapCustomAttributeFromResponse = (
  response: CustomAttributeResponse
): CustomAttribute => ({
  id: response.id,
  name: response.name,
  type: mapCustomAttributeTypeFromDTO(response.type),
  options: response.selectOptions?.map(mapSelectOption) ?? undefined,
});

export const mapAttributeValueFromResponse = (
  response: AttributeValueResponse
): CustomAttribute => ({
  id: response.attribute.id,
  name: response.attribute.name,
  type: mapCustomAttributeTypeFromDTO(response.attribute.type),
  value: response.value,
  selectedOptions: response.selectedOptions?.map(mapSelectOption),
  options: response.attribute.selectOptions?.map(mapSelectOption) ?? undefined,
});

export const mapCustomAttributesFromResponse = (
  responses: CustomAttributeResponse[]
): CustomAttribute[] => responses.map(mapCustomAttributeFromResponse);

/**
 * API request DTO → form data. The inverse of `mapCustomAttributesToDTO`, used to
 * restore the form from a previously-built draft (e.g. Create mode tab switching).
 * Select types map their `optionIds` back to string IDs; other types keep `value`.
 */
export const mapCustomAttributeDTOToFormData = (
  values: CreateAttributeValueRequest[],
  availableAttributes: CustomAttribute[]
): CustomAttributesFormData => {
  const result: CustomAttributesFormData = {};

  const attributeById = new Map<number, CustomAttribute>();
  availableAttributes.forEach(attr => attributeById.set(attr.id, attr));

  values.forEach(({ attributeId, optionIds, value }) => {
    const attribute = attributeById.get(attributeId);
    if (!attribute) return;

    const key = normalizeAttributeKey(attribute.name);

    if (isSelectType(attribute.type)) {
      const ids = (optionIds ?? []).map(String);
      result[key] = attribute.type === CustomAttributeType.Select ? (ids[0] ?? null) : ids;
    } else {
      result[key] = value ?? null;
    }
  });

  return result;
};

/**
 * Domain model → form data. Select types store option IDs as strings.
 */
export const mapCustomAttributesWithValuesToFormData = (
  attributes: CustomAttribute[]
): CustomAttributesFormData => {
  const result: CustomAttributesFormData = {};

  attributes.forEach(attr => {
    const key = normalizeAttributeKey(attr.name);

    if (isSelectType(attr.type) && attr.selectedOptions?.length) {
      if (attr.type === CustomAttributeType.Select) {
        result[key] = String(attr.selectedOptions[0].id);
      } else {
        result[key] = attr.selectedOptions.map(o => String(o.id));
      }
    } else {
      result[key] = attr.value ?? null;
    }
  });

  return result;
};

/**
 * Builds the initial custom-attributes form value covering EVERY available attribute,
 * seeded with the entity's current value where present and an empty default otherwise.
 *
 * Why this exists: `CustomAttributesFields` renders a field for every available attribute,
 * so React Hook Form registers a key for each one. If the initial form value only contains
 * keys for attributes that have a value (as `mapCustomAttributesWithValuesToFormData` does),
 * the initial value `{}` and RHF's registered `{ key: undefined, ... }` differ once normalized
 * (undefined → null), making the form appear dirty on open. Seeding all keys with the same
 * empty default RHF holds keeps the dirty comparison clean until a real edit.
 *
 * Empty default per type: `null` for scalars/single-select, `null` for multi-select too —
 * RHF registers unset fields as `undefined` (→ `null` after normalization), and the
 * MultiSelect's `defaultValue` does not write into form state.
 */
export const buildInitialCustomAttributesFormData = (
  availableAttributes: CustomAttribute[],
  entityValues: CustomAttribute[]
): CustomAttributesFormData => {
  const valuesByKey = mapCustomAttributesWithValuesToFormData(entityValues);

  const result: CustomAttributesFormData = {};
  availableAttributes.forEach(attr => {
    const key = normalizeAttributeKey(attr.name);
    result[key] = key in valuesByKey ? valuesByKey[key] : null;
  });

  return result;
};
